/**
 * What the user has done with the product tour, kept on the server
 * (GET/PUT /api/user/tours, apps/api/api/services/user_tour.py) so the tour
 * is offered once per person, on any device.
 */
import { queryOptions } from '@tanstack/react-query';
import { api } from '@/api/client';

/** The tour offered on its own. Screen tours are replayed on request and never stored. */
export const TOUR_ID = 'core';
/**
 * Raise when the tour changes enough to be worth offering again to people who
 * saw an earlier one. Copy edits and new steps for small features do not.
 */
export const TOUR_VERSION = 1;

export type TourOutcome = 'completed' | 'dismissed';
export type TourProgress = { version: number; outcome: TourOutcome; updated_at: string };
export type ToursResponse = Partial<Record<string, TourProgress>>;

export const tourApi = {
  tours: (signal?: AbortSignal) => api.get<ToursResponse>('/api/user/tours', { signal }),
  save: (outcome: TourOutcome) => api.put<TourProgress>('/api/user/tours', { tour_id: TOUR_ID, version: TOUR_VERSION, outcome }),
};

export const tourKeys = {
  all: ['tour'] as const,
  tours: () => [...tourKeys.all, 'tours'] as const,
};

export const toursQuery = () =>
  queryOptions({ queryKey: tourKeys.tours(), queryFn: ({ signal }) => tourApi.tours(signal), staleTime: Infinity });

/** Offer the tour to someone who never answered this version of it. Unknown (still loading, failed) is never an offer. */
export const shouldOffer = (tours: ToursResponse | undefined): boolean =>
  tours !== undefined && (tours[TOUR_ID]?.version ?? 0) < TOUR_VERSION;
